import { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminApp } from "@/hooks/useAdminApp";
import { useStateMachineValue } from "@/core/arch/state_machine";
import { ROUTES } from "@/routes";
import { StatusBadge } from "./components/StatusBadge";
import { CursorPagination } from "@/components/ui/data-table/CursorPagination";
import { Button } from "@/components/ui/base/button";
import { Input } from "@/components/ui/base/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { cn } from "@/lib/utils";
import { Plus, Loader2, ChevronRight, Search } from "lucide-react";
import type { ProvisionStatus } from "@/application/provisioning/types";

const FILTER_TABS: { key: ProvisionStatus | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "in_progress", label: "In Progress" },
  { key: "pending", label: "Pending" },
  { key: "cancelled", label: "Cancelled" },
];

const FLAG: Record<string, string> = {
  CA: "\u{1F1E8}\u{1F1E6}",
  US: "\u{1F1FA}\u{1F1F8}",
};

export function TerminalAccountsView() {
  const { provisioningListBloc } = useAdminApp();
  const [state] = useStateMachineValue(provisioningListBloc);
  const navigate = useNavigate();

  const [localSearchType, setLocalSearchType] = useState<
    "email" | "businessName"
  >(() => provisioningListBloc.state.search.type);
  const [localSearchQuery, setLocalSearchQuery] = useState(
    () => provisioningListBloc.state.search.query,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    provisioningListBloc.load();
  }, [provisioningListBloc]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchTypeChange = useCallback(
    (type: "email" | "businessName") => {
      setLocalSearchType(type);
      setLocalSearchQuery("");
      clearTimeout(debounceRef.current);
      void provisioningListBloc.setSearch({ type, query: "" });
    },
    [provisioningListBloc],
  );

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setLocalSearchQuery(query);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void provisioningListBloc.setSearch({
          type: localSearchType,
          query,
        });
      }, 300);
    },
    [provisioningListBloc, localSearchType],
  );

  const searchPlaceholder = `Search by ${localSearchType === "email" ? "email" : "business name"}…`;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-foreground text-2xl font-bold">
          Terminal Accounts
        </h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-0">
            <Select
              value={localSearchType}
              onValueChange={(v) =>
                handleSearchTypeChange(v as "email" | "businessName")
              }
            >
              <SelectTrigger
                size="sm"
                className="bg-muted h-9 rounded-r-none border-r-0 text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="businessName">Business name</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="text-subtle-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={searchPlaceholder}
                className="w-56 rounded-l-none pl-9"
                value={localSearchQuery}
                onChange={(e) => handleSearchQueryChange(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={() => navigate(ROUTES.NEW_ACCOUNT)}>
            <Plus className="mr-2 h-4 w-4" />
            New Account
          </Button>
        </div>
      </div>

      <div className="mb-4 flex gap-1">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => void provisioningListBloc.setStatusFilter(tab.key)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              state.statusFilter === tab.key
                ? "bg-foreground text-background"
                : "bg-card text-muted-foreground hover:bg-muted border",
              tab.key === "failed" &&
                state.statusFilter !== tab.key &&
                "text-red-600",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {state.loading && state.items.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="bg-card overflow-hidden rounded-lg border">
          <div className="bg-muted text-muted-foreground grid grid-cols-[2fr_2fr_0.8fr_1fr_1.5fr_0.5fr] border-b px-4 py-2.5 text-xs font-medium tracking-wide uppercase">
            <span>Business</span>
            <span>Contact</span>
            <span>Country</span>
            <span>Status</span>
            <span>Created</span>
            <span />
          </div>
          {state.error ? (
            <div className="px-4 py-8 text-center text-sm text-red-500">
              Failed to load accounts: {state.error}
            </div>
          ) : state.items.length === 0 ? (
            <div className="text-subtle-foreground px-4 py-8 text-center text-sm">
              No accounts found
            </div>
          ) : (
            state.items.map((item) => (
              <div
                key={item.id}
                onClick={() =>
                  navigate(ROUTES.ACCOUNT_DETAIL.replace(":id", item.id))
                }
                className={cn(
                  "border-border hover:bg-muted grid cursor-pointer grid-cols-[2fr_2fr_0.8fr_1fr_1.5fr_0.5fr] items-center border-b px-4 py-3 last:border-0",
                  item.status === "failed" && "bg-amber-50/50",
                )}
              >
                <span className="text-foreground font-medium">
                  {item.businessName}
                </span>
                <span className="text-muted-foreground text-sm">
                  {item.email}
                </span>
                <span>
                  {FLAG[item.businessCountry] ?? item.businessCountry}
                </span>
                <span>
                  <StatusBadge status={item.status} />
                </span>
                <span className="text-muted-foreground text-sm">
                  {new Date(item.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                <span className="text-right">
                  <ChevronRight className="inline h-4 w-4 text-blue-600" />
                </span>
              </div>
            ))
          )}
          <CursorPagination
            hasPrev={state.hasPrev}
            hasNext={state.hasNext}
            onPrev={() => void provisioningListBloc.prevPage()}
            onNext={() => void provisioningListBloc.nextPage()}
            loading={state.loading}
          />
        </div>
      )}
    </div>
  );
}
